import React, { useState } from 'react';

const ThankYouModal = ({ 
  isOpen, 
  contributor, 
  gift, 
  couple, 
  onClose,
  onThanked,
  formatPrice 
}) => {
  // Mensaje más largo para email
  const [emailMessage, setEmailMessage] = useState('');
  
  // Mensaje más corto para WhatsApp
  const [whatsappMessage, setWhatsappMessage] = useState('');

  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('email');

  // Resetear mensajes cuando cambia el contributor
  React.useEffect(() => {
    if (contributor && couple && gift) {
      setEmailMessage(
        `¡Gracias ${contributor.name || 'por tu hermoso regalo'}!\n\n` +
        `Tu aporte significa mucho para nosotros.\n\n` +
        `Tu gesto nos llena de alegría y hace que este momento sea aún más especial.\n\n` +
        `Con cariño,\n${couple.brideName || ''} & ${couple.groomName || ''}`
      );
      
      setWhatsappMessage(
        `¡Hola ${contributor.name || ''}! 💕\n\n` +
        `Queríamos agradecerte personalmente por tu regalo "${gift.name || ''}".\n\n` +
        `Tu gesto nos llena de alegría y hace que este momento sea aún más especial. ¡Gracias por ser parte de nuestro día!\n\n` +
        `Con mucho cariño,\n${couple.brideName || ''} & ${couple.groomName || ''} 💍`
      );
    }
  }, [contributor, couple, gift]);

  if (!isOpen) return null;

  // Validar número de teléfono (ya viene del contributor)
  const hasValidPhone = contributor?.phone && contributor.phone.replace(/\D/g, '').length >= 8;

  const handleSendEmail = async () => {
    if (!emailMessage.trim()) {
      alert('❌ Por favor escribí un mensaje');
      return;
    }

    setSending(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch('https://gift-sendthankyou-336444799661.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipientEmail: contributor.email,
          recipientName: contributor.name,
          message: emailMessage,
          coupleName: `${couple.brideName} & ${couple.groomName}`,
          giftName: gift.name,
          amount: contributor.amount
        })
      });

      const data = await response.json();

      if (data.success) {
        await markAsThanked();
        alert(`✅ Email enviado a ${contributor.name}`);
        onClose();
      } else {
        alert('❌ Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error sending thank you:', error);
      alert('❌ Error al enviar el email');
    } finally {
      setSending(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!whatsappMessage.trim()) {
      alert('❌ Por favor escribí un mensaje');
      return;
    }

    if (!hasValidPhone) {
      alert('❌ Este contribuyente no tiene número de teléfono válido');
      return;
    }

    // Limpiar número (solo dígitos)
    let phoneNumber = contributor.phone.replace(/\D/g, '');
    
    // Si no empieza con código de país, asumir Argentina (+54)
    if (!phoneNumber.startsWith('54') && phoneNumber.length <= 10) {
      phoneNumber = '54' + phoneNumber;
    }

    const text = encodeURIComponent(whatsappMessage);
    window.open(`https://wa.me/${phoneNumber}?text=${text}`, '_blank');

    // Marcar como agradecido
    await markAsThanked();
    onClose();
  };

  const markAsThanked = async () => {
    try {
      const token = localStorage.getItem('token');
      const markThankedData = {
        giftId: gift.id,
        contributorEmail: contributor.email
      };
      
      console.log('🔍 Marcando como agradecido:', markThankedData);
      
      const markResponse = await fetch('https://gift-markthanked-336444799661.us-central1.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(markThankedData)
      });
      
      const markData = await markResponse.json();
    //   console.log('📩 Respuesta giftMarkThanked:', markData);
      
      if (markData.success) {
        onThanked?.();
      } else {
        console.error('⚠️ No se pudo marcar como agradecido:', markData.error);
      }
    } catch (markError) {
      console.error('❌ Error al marcar como agradecido:', markError);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6 sm:p-8 animate-scale-in max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-neutral-900">
              Enviar agradecimiento
            </h2>
            <p className="text-neutral-600 mt-1">
              A: <span className="font-semibold">{contributor?.name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Gift info */}
        <div className="bg-gradient-to-r from-accent-50 to-primary-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-neutral-600 mb-1">Regalo:</p>
          <p className="font-semibold text-neutral-900">{gift?.name}</p>
          <p className="text-sm text-accent-600 font-semibold mt-2">
            Aporte: {formatPrice ? formatPrice(contributor?.amount || 0) : `$${contributor?.amount || 0}`}
          </p>
        </div>

        {/* Advertencia si ya fue agradecido */}
        {contributor?.thanked && (
          <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <span className="text-xl">⚠️</span>
              <div>
                <p className="font-semibold text-amber-900 mb-1">
                  Ya enviaste un agradecimiento
                </p>
                <p className="text-sm text-amber-800">
                  Anteriormente enviaste un mensaje a {contributor?.name}.
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  Podés enviar uno nuevo si querés, pero evitá duplicados innecesarios.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs para elegir método */}
        <div className="flex gap-2 mb-6 p-1 bg-neutral-100 rounded-xl">
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'email'
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <span>✉️</span>
            <span>Email</span>
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
              activeTab === 'whatsapp'
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span>WhatsApp</span>
            {!hasValidPhone && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                Sin tel.
              </span>
            )}
          </button>
        </div>

        {/* Contenido según tab activa */}
        {activeTab === 'email' ? (
          <>
            {/* Info de contacto email */}
            <div className="flex items-center gap-2 text-sm text-neutral-600 mb-4 bg-neutral-50 px-3 py-2 rounded-lg">
              <span>📧</span>
              <span>{contributor?.email}</span>
            </div>

            {/* Message input email */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Mensaje del email
              </label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={8}
                className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent resize-none"
                placeholder="Escribí tu mensaje de agradecimiento..."
              />
            </div>

            {/* Preview email */}
            <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 mb-6">
              <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                Vista previa del email
              </p>
              <div className="bg-white rounded-lg p-4 border border-neutral-200">
                <p className="text-sm text-neutral-600 mb-2">
                  Para: <span className="font-semibold text-neutral-900">{contributor?.email}</span>
                </p>
                <p className="text-sm text-neutral-600 mb-2">
                  Asunto: <span className="font-semibold text-neutral-900">
                    ¡Gracias por tu regalo! 💕 - {couple?.brideName} & {couple?.groomName}
                  </span>
                </p>
                <div className="mt-3 pt-3 border-t border-neutral-200">
                  <p className="text-sm text-neutral-700 whitespace-pre-line">
                    {emailMessage || 'Tu mensaje aparecerá aquí...'}
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons email */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 btn-secondary py-3"
                disabled={sending}
              >
                Cancelar
              </button>
              <button
                onClick={handleSendEmail}
                className="flex-1 btn-primary py-3"
                disabled={sending || !emailMessage.trim()}
              >
                {sending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    ✉️ Enviar email
                  </span>
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Info de contacto WhatsApp */}
            {hasValidPhone ? (
              <div className="flex items-center gap-2 text-sm text-neutral-600 mb-4 bg-green-50 px-3 py-2 rounded-lg">
                <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span className="text-green-700">{contributor?.phone}</span>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <span className="text-xl">📱</span>
                  <div>
                    <p className="font-semibold text-amber-900 mb-1">
                      Sin número de teléfono
                    </p>
                    <p className="text-sm text-amber-800">
                      {contributor?.name} no dejó su número de teléfono al hacer el regalo.
                    </p>
                    <p className="text-sm text-amber-700 mt-2">
                      Podés enviarle un email o contactarlo por otro medio.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Message input WhatsApp */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Mensaje de WhatsApp
              </label>
              <textarea
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                placeholder="Escribí tu mensaje de agradecimiento..."
                disabled={!hasValidPhone}
              />
              <p className="text-xs text-neutral-500 mt-2">
                💡 Tip: Los mensajes de WhatsApp suelen ser más cortos e informales
              </p>
            </div>

            {/* Preview WhatsApp */}
            <div className="bg-[#e5ddd5] rounded-xl p-4 mb-6">
              <p className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-3">
                Vista previa de WhatsApp
              </p>
              <div className="bg-[#dcf8c6] rounded-lg p-3 shadow-sm max-w-[85%] ml-auto">
                <p className="text-sm text-neutral-800 whitespace-pre-line">
                  {whatsappMessage || 'Tu mensaje aparecerá aquí...'}
                </p>
                <p className="text-[10px] text-neutral-500 text-right mt-1">
                  {new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} ✓✓
                </p>
              </div>
            </div>

            {/* Buttons WhatsApp */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 btn-secondary py-3"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendWhatsApp}
                className="flex-1 py-3 px-6 rounded-xl font-semibold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: hasValidPhone ? '#25D366' : '#9ca3af' }}
                disabled={!hasValidPhone || !whatsappMessage.trim()}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Abrir WhatsApp
                </span>
              </button>
            </div>

            {hasValidPhone && (
              <p className="text-xs text-center text-neutral-500 mt-4">
                Se abrirá WhatsApp Web/App con el mensaje listo para enviar
              </p>
            )}
          </>
        )}

        {/* Info general */}
        <p className="text-xs text-center text-neutral-400 mt-4 pt-4 border-t border-neutral-100">
          De parte de {couple?.brideName} & {couple?.groomName}
        </p>
      </div>
    </div>
  );
};

export default ThankYouModal;